/**
 * ADR-0116 §4.1/§7.1 — the console side of the model config-change seam,
 * SLIMMED to preview rendering (operations-family Phase 2 migration).
 *
 * The apply/validate/destructive rules moved hub-side (`ai/modelConfigChange.ts`
 * there): the editor's `applyModelConfigChange` (OperationEditor.tsx) now
 * flushes autosave, POSTs the delta through `change-apply`, and patches the
 * returned node config in place — the hub is the one applier for every AI-
 * directed mutation, and its `confirmation_required` response drives the
 * editor's confirm dialog. What remains here is what the console still RENDERS:
 * the delta summary for proposed-change cards (advisor + architect reach) and
 * the before/after diffs the applier reports back.
 *
 * The Tier-1 provisional/intent lifecycle (rough→refine→review) stays in the
 * editor/inspector — these types carry those fields verbatim.
 *
 * The TS type is identical to spec §7.1 (the single source of truth; the hub
 * defines the same shape as a JSON schema).
 */

export type ModelFamily = "regression" | "classification" | "anomaly" | "clustering";

export type ModelNodeKind = "model_train" | "model_predict" | "model_evaluate";

export type ModelConfigChange = {
  // model_train config (spec_model_train.config):
  set_family?: ModelFamily;
  set_algorithm?: string; // valid for the resulting family
  set_target?: string | null; // null clears; ignored for anomaly/clustering
  set_features?: string[]; // replace
  add_features?: string[];
  remove_features?: string[];
  set_tuning?: boolean; // hpo.enabled
  set_holdout?: number; // validation.holdout_fraction ∈ (0,1)
  set_contamination?: number; // anomaly ∈ (0,0.5)
  set_n_clusters?: number; // clustering ≥ 2
  set_k_range?: [number, number]; // clustering [lo,hi], 2 ≤ lo ≤ hi
  // ADR-0118 missing-value handling (numeric only); additive (one-click):
  set_missing_values?: { strategy?: "auto" | "impute" | "drop" | "error"; numeric?: "median" | "mean" | "constant" };
  // model_predict / model_evaluate binding (spec_model_predict / spec_model_evaluate):
  set_model_id?: string | null; // bind to a Model; null clears the binding
  set_version?: string; // "current" or a version number (as a string, e.g. "3")
  set_label_column?: string | null; // model_evaluate only; the true-label column (null/"" clears)
};

/** The binding spec for a model_predict / model_evaluate node. */
export type ModelBindingConfig = {
  model_id?: string | null;
  version?: number | "current";
  label_column?: string; // evaluate only
  [k: string]: unknown;
};

/** The `spec_model_train.config` blob. Carries the known training fields plus any
 *  provisional/intent fields (target_intent/feature_intent/provisional) which the
 *  seam preserves verbatim — the inspector's Confirm clears `provisional`. */
export type ModelTrainConfig = {
  algorithm_family?: string;
  algorithm?: string;
  target_column?: string;
  feature_columns?: string[];
  validation?: { strategy?: string; holdout_fraction?: number };
  hpo?: { enabled?: boolean };
  contamination?: number;
  n_clusters?: number;
  k_range?: [number, number];
  missing_values?: { strategy?: string; numeric?: string };
  [k: string]: unknown;
};

/** Result of the editor's applyModelConfigChange seam call (shared with the panel/card callers). */
export type ApplyModelConfigResult =
  | { ok: true; diff: ModelConfigDiff }
  | { ok: false; reason: string; cancelled?: boolean };

// ---------------------------------------------------------------------------
// Diff (preview rendering — computed from before vs the hub's applied state)
// ---------------------------------------------------------------------------

export type ModelConfigDiff = {
  /** Scalar field changes, business-labelled. */
  changes: Array<{ key: string; label: string; before: string; after: string }>;
  /** Feature list delta (for "features: + a, − b" rendering). */
  featuresAdded: string[];
  featuresRemoved: string[];
  /** True when nothing actually changed. */
  empty: boolean;
};

const FAMILY_LABEL: Record<string, string> = {
  regression: "Regression", classification: "Classification", anomaly: "Anomaly detection", clustering: "Clustering",
};
const MV_STRATEGY_LABEL: Record<string, string> = {
  auto: "Automatic", impute: "Fill values", drop: "Drop rows", error: "Fail if missing",
};
const show = (v: unknown): string => {
  if (v === undefined || v === null || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
};

export function computeConfigDiff(before: ModelTrainConfig, after: ModelTrainConfig): ModelConfigDiff {
  const changes: ModelConfigDiff["changes"] = [];
  const push = (key: string, label: string, b: unknown, a: unknown) => {
    if (show(b) !== show(a)) changes.push({ key, label, before: show(b), after: show(a) });
  };
  push("algorithm_family", "Type", FAMILY_LABEL[before.algorithm_family ?? ""] ?? before.algorithm_family, FAMILY_LABEL[after.algorithm_family ?? ""] ?? after.algorithm_family);
  push("algorithm", "Algorithm", before.algorithm, after.algorithm);
  push("target_column", "Target", before.target_column, after.target_column);
  push("tuning", "Automatic tuning", before.hpo?.enabled ? "on" : "off", after.hpo?.enabled ? "on" : "off");
  push("holdout", "Holdout", before.validation?.holdout_fraction, after.validation?.holdout_fraction);
  push("contamination", "Contamination", before.contamination, after.contamination);
  push("n_clusters", "Clusters", before.n_clusters, after.n_clusters);
  push("k_range", "Cluster range", before.k_range, after.k_range);
  push("missing_values", "Missing values", MV_STRATEGY_LABEL[before.missing_values?.strategy ?? "auto"] ?? before.missing_values?.strategy, MV_STRATEGY_LABEL[after.missing_values?.strategy ?? "auto"] ?? after.missing_values?.strategy);
  push("missing_numeric", "Numeric fill", before.missing_values?.numeric, after.missing_values?.numeric);

  const bf = new Set(Array.isArray(before.feature_columns) ? before.feature_columns : []);
  const af = new Set(Array.isArray(after.feature_columns) ? after.feature_columns : []);
  const featuresAdded = [...af].filter((f) => !bf.has(f));
  const featuresRemoved = [...bf].filter((f) => !af.has(f));

  return { changes, featuresAdded, featuresRemoved, empty: changes.length === 0 && featuresAdded.length === 0 && featuresRemoved.length === 0 };
}

/** Business-language summary of a raw delta (no before-config needed) — for the
 *  advisor's proposed-change card and the architect reach card, which show the
 *  proposed change before the hub seam computes the real before/after on Apply. */
export function summarizeChange(change: ModelConfigChange, resolveModelName?: (id: string) => string | null): string {
  const parts: string[] = [];
  if (change.set_family) parts.push(`set type to ${FAMILY_LABEL[change.set_family] ?? change.set_family}`);
  if (change.set_algorithm) parts.push(`use ${change.set_algorithm}`);
  if (change.set_target !== undefined) parts.push(!change.set_target ? "clear the target" : `predict ${change.set_target}`);
  if (change.set_features) parts.push(`set inputs to ${change.set_features.join(", ")}`);
  if (change.add_features?.length) parts.push(`add inputs ${change.add_features.join(", ")}`);
  if (change.remove_features?.length) parts.push(`remove inputs ${change.remove_features.join(", ")}`);
  if (change.set_tuning !== undefined) parts.push(`turn automatic tuning ${change.set_tuning ? "on" : "off"}`);
  if (change.set_holdout !== undefined) parts.push(`set holdout to ${change.set_holdout}`);
  if (change.set_contamination !== undefined) parts.push(`set expected outlier share to ${change.set_contamination}`);
  if (change.set_n_clusters !== undefined) parts.push(`set clusters to ${change.set_n_clusters}`);
  if (change.set_k_range) parts.push(`set cluster range to ${change.set_k_range[0]}–${change.set_k_range[1]}`);
  if (change.set_missing_values !== undefined) {
    const mv = change.set_missing_values;
    const bits: string[] = [];
    if (mv.strategy) bits.push(`handle missing values: ${MV_STRATEGY_LABEL[mv.strategy] ?? mv.strategy}`);
    if (mv.numeric) bits.push(`fill numbers with ${mv.numeric}`);
    if (bits.length) parts.push(bits.join(", "));
  }
  if (change.set_model_id !== undefined) parts.push(change.set_model_id ? `bind model ${resolveModelName?.(change.set_model_id) ?? "the selected model"}` : "clear the model binding");
  if (change.set_version !== undefined) parts.push(`set version to ${change.set_version}`);
  if (change.set_label_column !== undefined) parts.push(change.set_label_column ? `set label column to ${change.set_label_column}` : "clear the label column");
  return parts.length ? parts.join("; ") : "no change";
}

/** Before/after diff for a binding change; resolves model_id → name (never a UUID). */
export function computeBindingDiff(
  before: ModelBindingConfig,
  after: ModelBindingConfig,
  kind: ModelNodeKind,
  resolveModelName: (id: string) => string | null
): ModelConfigDiff {
  const changes: ModelConfigDiff["changes"] = [];
  const modelName = (id: string | null | undefined) => (id ? resolveModelName(id) ?? "a model" : "—");
  const bm = modelName(before.model_id);
  const am = modelName(after.model_id);
  if (bm !== am) changes.push({ key: "model", label: "Model", before: bm, after: am });
  const bv = String(before.version ?? "current");
  const av = String(after.version ?? "current");
  if (bv !== av) changes.push({ key: "version", label: "Version", before: bv, after: av });
  if (kind === "model_evaluate") {
    const bl = before.label_column ?? "";
    const al = after.label_column ?? "";
    if (bl !== al) changes.push({ key: "label", label: "Label column", before: bl || "—", after: al || "—" });
  }
  return { changes, featuresAdded: [], featuresRemoved: [], empty: changes.length === 0 };
}
